// libraries
import React, { useState, useRef } from 'react'

// components
import Section from '/react4xp/components/Section'
import Button from '/react4xp/components/Button'
import Link from '/react4xp/components/Link'
import Form from '/react4xp/components/Form/Form'
import Input from '/react4xp/components/Form/Input'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Popup from '/react4xp/components/Popup'

// prop types
interface EditProfileProps {
    strings: {
        profileHeader: string
        profileIntroBefore: string
        profileIntroAfter: string

        profileSuccessPopupClose: string
        profileSuccessPopupHeader: string
        profileSuccessPopupContent: string

        profileErrorPopupClose: string
        profileErrorPopupHeader: string
        profileErrorPopupContent: string

        profileFirstname: string
        profileLastname: string
        profileEmail: string
        profilePhone: string
        profileNewsletter: string

        validationRequired: string

        profileSave: string
        profileDelete: string

        profileDeletePopupClose: string
        profileDeletePopupHeader: string
        profileDeletePopupContent: string
        profileDeletePopupCancel: string
        profileDeletePopupConfirm: string

        profileLogout: string
    }
    user: {
        firstName: string
        lastName: string
        email: string
        phone: string
        consentNewsletter: boolean
    }
    editServiceUrl: string
    deleteServiceUrl: string
    logoutUrl: string
}

export default function EditProfile({
    strings,
    user,
    editServiceUrl,
    deleteServiceUrl,
    logoutUrl,
}: EditProfileProps) {

    // refs
    const popupExcludeConfirmation = useRef<HTMLAnchorElement>(null)

    // useState to make the modal invisible
    const [renderExcludeConfirmation, setRenderExcludeConfirmation] = useState(false)

    // close modal
    const closeExcludeModal = () => {
        setRenderExcludeConfirmation(false)
    }

    // open modal
    const openExcludeModal = () => {
        setRenderExcludeConfirmation(true)
        setTimeout(() => {
            popupExcludeConfirmation?.current?.click()
        }, 100)
    }

    return (
        <Section bgColor='red' paddingTop='small' paddingBottom='small'>
            <div className='container'>
                <div className='row'>
                    <div className='col-md-10 offset-md-1 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3'>
                        <div className='page-user__content bg-white'>

                            <h1 className='page-user__title h2'>
                                {strings.profileHeader}
                            </h1>

                            <p className='page-user__desc text-big'>
                                {strings.profileIntroBefore} {(user.firstName + ' ' + user.lastName).trim()}{strings.profileIntroAfter}
                            </p>

                            <Form
                                endpoint={editServiceUrl}
                                className='page-user__form'
                                onSuccess={() => {
                                    window.location.reload()
                                }}
                                popupSuccessContent={{ // TODO: Check if it will be necessary
                                    hasCloseButton: true,
                                    closeButtonText: strings.profileSuccessPopupClose,
                                    title: strings.profileSuccessPopupHeader,
                                    content: strings.profileSuccessPopupContent,
                                    actionButtons: {
                                        primary: {
                                            text: strings.profileSuccessPopupClose
                                        }
                                    }
                                }}
                                popupErrorContent={{
                                    hasCloseButton: true,
                                    closeButtonText: strings.profileErrorPopupClose,
                                    title: strings.profileErrorPopupHeader,
                                    content: strings.profileErrorPopupContent,
                                    actionButtons: {
                                        primary: {
                                            text: strings.profileErrorPopupClose
                                        }
                                    }
                                }}
                            >
                                <Input
                                    name='Fornavn'
                                    id='edit-profile-first-name'
                                    label={strings.profileFirstname}
                                    type='text'
                                    disabled
                                    value={user.firstName}
                                    size='large'
                                />

                                <Input
                                    name='lastname'
                                    id='edit-profile-last-name'
                                    label={strings.profileLastname}
                                    type='text'
                                    disabled
                                    value={user.lastName}
                                    className='page-user__margin-bottom'
                                    size='large'
                                />

                                <Input
                                    name='email'
                                    id='edit-profile-email'
                                    label={strings.profileEmail}
                                    type='email'
                                    value={user.email}
                                    disabled
                                />

                                <Input
                                    name='phone'
                                    id='edit-profile-phone'
                                    label={strings.profilePhone}
                                    type='tel'
                                    value={user.phone}
                                    className='page-user__margin-bottom'
                                    size='medium'
                                />

                                <Checkbox
                                    id='edit-profile-newsletter'
                                    name='newsletter'
                                    checked={user.consentNewsletter}
                                    className='page-user__margin-bottom'
                                >
                                    {strings.profileNewsletter}
                                </Checkbox>

                                <div className='page-user__two-buttons'>

                                    <Button
                                        color='black'
                                        hover='red'
                                        type='submit'
                                        noUnderline
                                    >
                                        {strings.profileSave}
                                    </Button>

                                    <Link
                                        type='button'
                                        text={strings.profileDelete}
                                        onClick={openExcludeModal}
                                    />

                                    {renderExcludeConfirmation &&
                                        <Popup
                                            size='medium'
                                            ref={popupExcludeConfirmation}
                                            hasCloseButton
                                            closeButtonText={strings.profileDeletePopupClose}
                                            title={strings.profileDeletePopupHeader}
                                            content={strings.profileDeletePopupContent}
                                            className='popup--exclude-popup'
                                            actionButtons={{
                                                primary: {
                                                    text: strings.profileDeletePopupCancel,
                                                    onClick: () => {
                                                        closeExcludeModal()
                                                    }
                                                },
                                                secondary: {
                                                    text: strings.profileDeletePopupConfirm,
                                                    onClick: () => {
                                                        window.location.href = deleteServiceUrl
                                                    }
                                                }
                                            }}
                                            onClose={closeExcludeModal}
                                        />
                                    }

                                </div>

                                <Button
                                    href={logoutUrl}
                                    color='transparent'
                                    hover='blue'
                                    hasBorders
                                    noUnderline
                                >
                                    {strings.profileLogout}
                                </Button>

                            </Form>

                        </div>
                    </div>
                </div>
            </div>
        </Section>
    )
}